import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LogosService, LogoTest } from '@core/services/logos/logos.service';

const jwtHelper = new JwtHelperService();

@Component({
  selector: 'app-logo-tester',
  templateUrl: './logo-tester.component.html',
  styleUrls: ['./logo-tester.component.scss'],
})
export class LogoTesterComponent implements OnInit {
  @Input() programId: string = '';

  logoForm!: FormGroup;
  logoTests: LogoTest[] = [];
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  // Paginación
  currentPage = 1;
  limit = 10;
  totalLogos = 0;
  filterStatus = '';

  constructor(
    private fb: FormBuilder,
    private logosService: LogosService,
  ) {}

  private getUserId(): string {
    const token = localStorage.getItem('token_conhector') || '';
    if (!token) return '';
    try {
      const decoded: any = jwtHelper.decodeToken(token);
      return decoded?.id || decoded?.uid || decoded?._id || decoded?.sub || '';
    } catch {
      return '';
    }
  }

  ngOnInit(): void {
    this.initForm();
    this.loadLogos();
  }

  private initForm(): void {
    this.logoForm = this.fb.group({
      phone_number: [
        '',
        [Validators.required, Validators.pattern(/^\+?[0-9]{10,15}$/)],
      ],
      brand_name: ['', Validators.required],
      logo_description: ['', Validators.required],
      preferred_colors: [''],
    });
  }

  onSubmit(): void {
    if (!this.logoForm.valid || !this.programId) {
      this.errorMessage = 'Completa todos los campos requeridos';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const request = {
      program_id: this.programId,
      phone_number: this.logoForm.get('phone_number')?.value,
      message_id: `${this.logoForm.get('phone_number')?.value}_${Date.now()}`,
      text: this.buildLogoText(),
      locale: 'es',
      user_id: this.getUserId(),
    };

    this.logosService.submitLogoTest(request).subscribe(
      (response) => {
        this.isLoading = false;
        if (response.ok) {
          this.successMessage = `✅ Solicitud enviada. Job ID: ${response.result.job_id}`;
          this.logoForm.reset();
          setTimeout(() => {
            this.loadLogos();
          }, 1000);
        } else {
          this.errorMessage = response.msg;
        }
      },
      (error) => {
        this.isLoading = false;
        this.errorMessage = `Error: ${error.error?.msg || error.message}`;
      },
    );
  }

  loadLogos(): void {
    if (!this.programId) return;

    this.logosService
      .getLogosByProgram(
        this.programId,
        this.currentPage,
        this.limit,
        this.filterStatus,
      )
      .subscribe(
        (response) => {
          this.logoTests = response.results;
          this.totalLogos = response.total;
        },
        (error) => {
          console.error('Error loading logos:', error);
        },
      );
  }

  pollLogoStatus(jobId: string): void {
    this.logosService.pollLogo(jobId).subscribe(
      (response) => {
        if (response.ok) {
          this.successMessage = `✅ Logo listo: ${response.result.logo_image_url}`;
        } else {
          this.errorMessage = `Estado: ${response.result.status}`;
        }
        setTimeout(() => {
          this.loadLogos();
        }, 500);
      },
      (error) => {
        this.errorMessage = `Error: ${error.error?.msg || error.message}`;
      },
    );
  }

  downloadImage(imageUrl: string): void {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `logo_${Date.now()}.png`;
    link.click();
  }

  shareImage(logo: LogoTest): void {
    if (!logo.logo_image_url) return;

    const text = `🎨 Logo generado para ${this.getLoboBrandFromText(logo.text)}\n\n${logo.logo_image_url}`;

    if (navigator.share) {
      navigator.share({
        title: 'Logo Generado',
        text: text,
        url: window.location.href,
      });
    } else {
      // Fallback: Copiar a clipboard
      navigator.clipboard.writeText(text);
      this.successMessage = 'Enlace copiado al portapapeles';
    }
  }

  copyJobId(jobId: string): void {
    navigator.clipboard.writeText(jobId);
    this.successMessage = 'Job ID copiado';
  }

  getStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      processing: '⏳ Procesando',
      done: '✅ Listo',
      failed: '❌ Error',
      pending: '⏹️ Pendiente',
    };
    return labels[status] || status;
  }

  getLoboBrandFromText(text: string): string {
    const match = text.match(/Marca:\s*([^\n]+)/);
    return match ? match[1] : 'Sin marca';
  }

  getLogoDescFromText(text: string): string {
    const match = text.match(/Descripción:\s*([^\n]+)/);
    return match ? match[1] : 'Sin descripción';
  }

  private buildLogoText(): string {
    const brand = this.logoForm.get('brand_name')?.value;
    const desc = this.logoForm.get('logo_description')?.value;
    const colors = this.logoForm.get('preferred_colors')?.value;

    let text = `Marca: ${brand}\n\nDescripción: ${desc}`;
    if (colors) {
      text += `\n\nColores: ${colors}`;
    }
    return text;
  }

  nextPage(): void {
    this.currentPage++;
    this.loadLogos();
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadLogos();
    }
  }

  get totalPages(): number {
    return Math.ceil(this.totalLogos / this.limit);
  }
}
